import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory, useLocation, useParams, Link } from 'react-router-dom'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { useDropzone } from 'react-dropzone'
import { toast } from 'react-toastify'
import { Box, Group, Anchor, Alert, Portal } from '@mantine/core'
import { AppNumberInput, AppSelect } from '../../../app/common/ui'
import MyTextInput from '../../../app/common/form/MyTextInput'
import MyTextArea from '../../../app/common/form/MyTextArea'
import RichTextEditor from '../../../app/common/form/RichTextEditor'
import Breadcrumb from '../../../app/common/Breadcrumb'
import LoadingComponent from '../../../app/layout/LoadingComponents'
import {
  TagPicker,
  AppButton,
  AppCard,
  AppText,
  BASE_POST_TYPE_OPTIONS
} from '../../../app/common/ui'
import { useStore } from '../../../app/stores/store'
import { PostFormValues, type PostType } from '../../../app/models/post'
import agent from '../../../app/api/agent'
import { TagTargetType } from '../../../app/models/tag'
import type { TagDto } from '../../../app/models/tag'
import { canCreateCommunityPost, MIN_AMMO_SCORE_TO_CREATE_POST } from '../../../app/common/constants/reputation'
import type { CoverImageMode } from './postDraft'
import {
  saveDraft,
  loadDraft,
  clearDraft,
  writeResumeBootstrap,
  readResumeBootstrap
} from './postDraft'

const REGISTRY_ESCAPE_QUERY = '?returnTo=createPost&linkedAfterCreate=true'

function readPostFormMount (search: string, isAdmin: boolean): {
  formValues: PostFormValues;
  coverImageMode: CoverImageMode;
  submitAfterRestore: boolean;
  linkedModelOption?: { value: string; label: string };
  linkedAmmoOption?: { value: string; label: string };
  resumeNotice: 'success' | 'warning' | 'none';
} {
  const resume = readResumeBootstrap()
  if (resume) {
    if (resume.formValues.type === 4 && !isAdmin) resume.formValues.type = 0
    return {
      formValues: resume.formValues,
      coverImageMode: resume.coverImageMode,
      submitAfterRestore: resume.submitAfterRestore,
      linkedModelOption: resume.linkedModelOption,
      linkedAmmoOption: resume.linkedAmmoOption,
      resumeNotice: resume.resumeNotice
    }
  }
  const qp = new URLSearchParams(search)
  const preselectedModelId = qp.get('firearmModelId')
  const preselectedType = qp.get('type')
  const v = new PostFormValues()
  if (preselectedModelId) v.firearmModelId = preselectedModelId
  if (preselectedType) {
    const parsed = parseInt(preselectedType, 10)
    if (parsed === 4 && !isAdmin) v.type = 0
    else if (parsed >= 0 && parsed <= 4) v.type = parsed as PostType
  }
  return {
    formValues: v,
    coverImageMode: 'url',
    submitAfterRestore: false,
    resumeNotice: 'none'
  }
}

const validationSchema = Yup.object({
  title: Yup.string().required('Title is required').max(200, 'Max 200 characters'),
  body: Yup.string().required('Body is required'),
  rating: Yup.number().min(1, '1–5').max(5, '1–5').nullable(),
  pros: Yup.string().max(2000, 'Max 2000 characters').nullable(),
  cons: Yup.string().max(2000, 'Max 2000 characters').nullable(),
  verdict: Yup.string().max(500, 'Max 500 characters').nullable()
})

function CoverImageSection ({
  mode,
  onModeChange,
  coverImageUrl,
  setFieldValue,
  uploadLoading,
  onUploadStart,
  onUploadEnd
}: {
  mode: CoverImageMode;
  onModeChange: (m: CoverImageMode) => void;
  coverImageUrl: string;
  setFieldValue: (field: string, value: string) => void;
  uploadLoading: boolean;
  onUploadStart: () => void;
  onUploadEnd: () => void;
}) {
  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0]
      if (!file) return
      onUploadStart()
      try {
        const result = await agent.Posts.uploadCover(file)
        setFieldValue('coverImageUrl', result?.url ?? '')
        onUploadEnd()
      } catch (error) {
        toast.error('Failed to upload cover image')
        onUploadEnd()
      }
    },
    [setFieldValue, onUploadStart, onUploadEnd]
  )
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: 'image/jpeg,image/jpg,image/png,image/gif,image/webp',
    maxFiles: 1,
    disabled: uploadLoading
  })

  const dzStyles: React.CSSProperties = {
    border: 'dashed 3px var(--mantine-color-gray-2)',
    borderRadius: '5px',
    padding: '24px',
    textAlign: 'center',
    cursor: uploadLoading ? 'wait' : 'pointer',
    opacity: uploadLoading ? 0.7 : 1
  }
  const dzActive = { borderColor: 'var(--mantine-color-navy-6)' }

  return (
    <Box mb="md">
      <AppText size="sm" weight={500} mb={4}>Cover image (optional)</AppText>
      <Group grow mb="xs">
        <AppButton
          type="button"
          variant={mode === 'url' ? 'filled' : 'light'}
          color="gray"
          onClick={() => onModeChange('url')}
        >
          Paste URL
        </AppButton>
        <AppButton
          type="button"
          variant={mode === 'upload' ? 'filled' : 'light'}
          color="gray"
          onClick={() => onModeChange('upload')}
        >
          Upload image
        </AppButton>
      </Group>
      {mode === 'url'
        ? (
        <MyTextInput
          name="coverImageUrl"
          placeholder="Cover image URL (optional)"
          label=""
        />
          )
        : (
        <Box {...getRootProps()} sx={{ ...dzStyles, ...(isDragActive ? dzActive : {}) }}>
          <input {...getInputProps()} />
          {uploadLoading
            ? (
            <AppText size="sm" color="dimmed">Uploading…</AppText>
              )
            : (
            <>
              <AppText size="sm" color="dimmed">Drop an image here, or click to select</AppText>
              {coverImageUrl && (
                <Box mt="sm">
                  <img
                    src={coverImageUrl}
                    alt="Cover preview"
                    style={{ maxHeight: 120, maxWidth: '100%', borderRadius: 4 }}
                  />
                </Box>
              )}
            </>
              )}
        </Box>
          )}
    </Box>
  )
}

export default observer(function PostForm () {
  const history = useHistory()
  const location = useLocation()
  const { blogStore, firearmModelStore, partStore, userStore } = useStore()
  const { createPost, updatePost, loadPost, loadingInitial } = blogStore
  const { id } = useParams<{ id: string }>()
  const bootstrapRef = useRef<ReturnType<typeof readPostFormMount> | null>(null)
  if (bootstrapRef.current === null) {
    bootstrapRef.current = readPostFormMount(location.search, userStore.isAdmin)
  }
  const boot = bootstrapRef.current
  const [postTypeOptions, setPostTypeOptions] = useState<{ value: string; label: string }[]>(
    () => BASE_POST_TYPE_OPTIONS
  )
  const [formValues, setFormValues] = useState<PostFormValues>(boot.formValues)

  useEffect(() => {
    let cancelled = false
    agent.Posts.getCreatableTypes()
      .then((types) => {
        if (cancelled || types.length === 0) return
        const options = types.map((t) => ({ value: String(t.value), label: t.label }))
        setPostTypeOptions(options)
        setFormValues((prev) => {
          if (options.some((o) => o.value === String(prev.type))) return prev
          return new PostFormValues({ ...prev, type: 0 })
        })
      })
      .catch(() => { /* keep BASE_POST_TYPE_OPTIONS fallback */ })
    return () => {
      cancelled = true
    }
  }, [])
  const [coverImageMode, setCoverImageMode] = useState<CoverImageMode>(boot.coverImageMode)
  const [uploadCoverLoading, setUploadCoverLoading] = useState(false)
  const [modelOptions, setModelOptions] = useState<{ value: string; label: string }[]>([])
  const [partOptions, setPartOptions] = useState<{ value: string; label: string }[]>([])
  const [ammoProductOptions, setAmmoProductOptions] = useState<{ value: string; label: string }[]>([])
  const [submitAfterRestore] = useState(boot.submitAfterRestore)
  const [linkedExtraOption] = useState<{ value: string; label: string } | undefined>(
    () => boot.linkedModelOption
  )
  const [linkedAmmoOption] = useState<{ value: string; label: string } | undefined>(
    () => boot.linkedAmmoOption
  )
  const [resumeBanner, setResumeBanner] = useState<'success' | 'warning' | null>(() =>
    boot.resumeNotice === 'none' ? null : boot.resumeNotice
  )
  const isEdit = !!id

  const handleAddFirearmEscape = (values: PostFormValues) => {
    saveDraft(values, coverImageMode)
    history.push(`/registry/new${REGISTRY_ESCAPE_QUERY}`)
  }

  const handleAddAmmoEscape = (values: PostFormValues) => {
    saveDraft(values, coverImageMode)
    history.push('/ammo/submit?returnTo=createPost&linkedAfterCreate=true')
  }

  useEffect(() => {
    if (id) {
      loadPost(id).then((post) => post && setFormValues(new PostFormValues(post)))
    }
  }, [id, loadPost])

  useEffect(() => {
    if (isEdit) return
    let cancelled = false
    firearmModelStore.loadModels().then(async () => {
      if (cancelled) return
      const approved = firearmModelStore.modelsByDate.filter((m) => m.status === 1)
      const opts = approved.map((m) => ({ value: m.id, label: `${m.make} ${m.model}` }))
      if (linkedExtraOption && !opts.some((o) => o.value === linkedExtraOption.value)) {
        opts.unshift(linkedExtraOption)
      }
      const pendingId = formValues.firearmModelId
      if (
        pendingId &&
        !opts.some((o) => o.value === pendingId) &&
        (!linkedExtraOption || linkedExtraOption.value !== pendingId)
      ) {
        try {
          const m = await agent.FirearmModels.details(pendingId)
          if (!cancelled && m) {
            opts.unshift({
              value: m.id,
              label: `${m.make} ${m.model} (pending review)`
            })
          }
        } catch {
          if (!cancelled) {
            opts.unshift({
              value: pendingId,
              label: 'Your model (pending review)'
            })
          }
        }
      }
      if (!cancelled) setModelOptions(opts)
    })
    partStore.loadParts().then(() => {
      if (cancelled) return
      const approved = partStore.partsByDate.filter((p) => p.status === 1)
      setPartOptions(approved.map((p) => ({ value: p.id, label: p.partNumber ? `${p.name} (${p.partNumber})` : p.name })))
    })
    return () => {
      cancelled = true
    }
  }, [isEdit, firearmModelStore, partStore, formValues.firearmModelId, linkedExtraOption])

  useEffect(() => {
    if (isEdit) return
    let cancelled = false
    if (linkedAmmoOption && !ammoProductOptions.some((o) => o.value === linkedAmmoOption.value)) {
      const opts = [linkedAmmoOption]
      setAmmoProductOptions(opts)
    }
    const pendingAmmoId = formValues.ammoProductId
    if (
      pendingAmmoId &&
      !ammoProductOptions.some((o) => o.value === pendingAmmoId) &&
      (!linkedAmmoOption || linkedAmmoOption.value !== pendingAmmoId)
    ) {
      (async () => {
        try {
          const a = await agent.AmmoProducts.details(pendingAmmoId)
          if (!cancelled && a) {
            setAmmoProductOptions([{
              value: a.id,
              label: `${a.brand} ${a.name} (pending review)`
            }])
          }
        } catch {
          if (!cancelled) {
            setAmmoProductOptions([{
              value: pendingAmmoId,
              label: 'Your ammo product (pending review)'
            }])
          }
        }
      })()
    }
    return () => {
      cancelled = true
    }
  }, [isEdit, formValues.ammoProductId, linkedAmmoOption])

  useEffect(() => {
    if (window.location.pathname !== '/createPost') return
    const params = new URLSearchParams(window.location.search)
    if (params.get('resumeDraft') !== 'true') return

    const linkedFirearmId = params.get('linkedFirearmId')
    const linkedAmmoProductId = params.get('linkedAmmoProductId')

    const finish = async () => {
      const draft = loadDraft()
      if (!draft) {
        writeResumeBootstrap({
          formValues: new PostFormValues(),
          coverImageMode: 'url',
          submitAfterRestore: false,
          resumeNotice: 'warning'
        })
        clearDraft()
        history.replace('/createPost')
        return
      }

      const nextValues = new PostFormValues(draft.values)
      let linkedOption: { value: string; label: string } | undefined
      let linkedAmmoOpt: { value: string; label: string } | undefined

      if (linkedFirearmId) {
        nextValues.firearmModelId = linkedFirearmId
        try {
          const m = await agent.FirearmModels.details(linkedFirearmId)
          linkedOption = {
            value: m.id,
            label: `${m.make} ${m.model} (pending review)`
          }
        } catch {
          linkedOption = {
            value: linkedFirearmId,
            label: 'Your model (pending review)'
          }
        }
      }

      if (linkedAmmoProductId) {
        nextValues.ammoProductId = linkedAmmoProductId
        try {
          const a = await agent.AmmoProducts.details(linkedAmmoProductId)
          linkedAmmoOpt = {
            value: a.id,
            label: `${a.brand} ${a.name} (pending review)`
          }
        } catch {
          linkedAmmoOpt = {
            value: linkedAmmoProductId,
            label: 'Your ammo product (pending review)'
          }
        }
      }

      writeResumeBootstrap({
        formValues: nextValues,
        coverImageMode: draft.coverImageMode,
        submitAfterRestore: true,
        linkedModelOption: linkedOption,
        linkedAmmoOption: linkedAmmoOpt,
        resumeNotice: 'success'
      })
      clearDraft()
      history.replace('/createPost')
    }

    void finish()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resume only from initial URL (Step 2c)
  }, [])

  useEffect(() => {
    if (!resumeBanner) return
    const t = window.setTimeout(() => setResumeBanner(null), 8000)
    return () => window.clearTimeout(t)
  }, [resumeBanner])

  const handleSubmit = async (values: PostFormValues) => {
    if (!isEdit) {
      const newId = await createPost(values)
      if (newId) {
        toast.info('Submitted! Your content has been submitted and is being reviewed before publishing.', { autoClose: 6000 })
        history.push(`/blog/${newId}`)
      }
    } else {
      await updatePost({ ...values, id: id! })
      history.push(`/blog/${id}`)
    }
  }

  if (loadingInitial && isEdit) {
    return <LoadingComponent content="Loading post..." />
  }

  if (!isEdit && userStore.isLoggedIn && !canCreateCommunityPost(userStore.user)) {
    return (
      <Box sx={{ fontFamily: 'DM Sans, sans-serif', maxWidth: 720, margin: '0 auto', padding: 24 }}>
        <Breadcrumb items={[{ label: 'Blog', href: '/blog' }, { label: 'New post' }]} />
        <AppCard padding="lg" mt="md" withBorder>
          <AppText size="lg" weight={600}>
            AmmoScore required to post
          </AppText>
          <AppText size="sm" color="dimmed" mt="sm">
            You need at least {MIN_AMMO_SCORE_TO_CREATE_POST} AmmoScore to publish blog posts, reviews, ownership tips, and
            discussions. Comment, vote, and contribute elsewhere to build your score.
          </AppText>
          <AppButton component={Link} to="/blog" mt="lg" color="ember" size="sm">
            Back to Blog
          </AppButton>
        </AppCard>
      </Box>
    )
  }

  const breadcrumbItems = [
    { label: 'Blog', href: '/blog' },
    isEdit && formValues.title
      ? { label: formValues.title, href: `/blog/${id}` }
      : { label: 'New post' }
  ].filter(Boolean) as { label: string; href?: string }[]

  return (
    <Box
      sx={{
        fontFamily: 'DM Sans, sans-serif',
        maxWidth: 720,
        margin: '0 auto',
        padding: 24
      }}
    >
      {resumeBanner === 'success' && (
        <Portal>
          <Box
            sx={{
              position: 'fixed',
              top: 72,
              right: 16,
              zIndex: 9999,
              maxWidth: 380
            }}
          >
            <Alert
              color="green"
              withCloseButton
              onClose={() => setResumeBanner(null)}
              title="Draft restored"
            >
              Draft restored! Your firearm has been linked and is pending review.
            </Alert>
          </Box>
        </Portal>
      )}
      {resumeBanner === 'warning' && (
        <Portal>
          <Box
            sx={{
              position: 'fixed',
              top: 72,
              right: 16,
              zIndex: 9999,
              maxWidth: 380
            }}
          >
            <Alert
              color="yellow"
              withCloseButton
              onClose={() => setResumeBanner(null)}
              title="Draft not found"
            >
              Draft expired or not found — please refill the form.
            </Alert>
          </Box>
        </Portal>
      )}
      <Breadcrumb items={breadcrumbItems} />
      <Group position="apart" mb="lg">
        <AppText size="xl" weight={600}>{isEdit ? 'Edit post' : 'New post'}</AppText>
        <AppButton component={Link} to={isEdit ? `/blog/${id}` : '/blog'} variant="subtle" size="sm">
          Cancel
        </AppButton>
      </Group>
      <AppCard
        padding="lg"
        sx={{
          background: 'var(--gs-surface)',
          border: '1px solid var(--gs-border)',
          borderRadius: 8
        }}
      >
      <Formik
        validationSchema={validationSchema}
        enableReinitialize
        initialValues={formValues}
        onSubmit={handleSubmit}
      >
        {({ handleSubmit, isValid, isSubmitting, dirty, values, setFieldValue }) => (
          <Form onSubmit={handleSubmit}>
            <MyTextInput name="title" placeholder="Post title" label="Title" />
            {!isEdit && (
              <>
                <Box mb="md">
                  <AppText size="sm" weight={500} mb={4}>Type</AppText>
                  <AppSelect
                    data={postTypeOptions}
                    value={String(values.type ?? 0)}
                    onChange={(v) => setFieldValue('type', v ? parseInt(v, 10) : 0)}
                    placeholder="Post type"
                  />
                </Box>
                {(values.type === 1 || values.type === '1') && (
                  <Box mb="md">
                    <AppText size="sm" weight={500} mb={4}>Rating (1–5)</AppText>
                    <NumberInput
                      min={1}
                      max={5}
                      value={values.rating ?? undefined}
                      onChange={(v) => setFieldValue('rating', v ?? null)}
                      placeholder="1–5"
                    />
                  </Box>
                )}
                <Box mb="md">
                  <AppText size="sm" weight={500} mb={4}>
                    Firearm model (optional)
                  </AppText>
                  <Anchor
                    component="button"
                    type="button"
                    onClick={() => handleAddFirearmEscape(values)}
                    sx={{
                      display: 'block',
                      textAlign: 'left',
                      marginBottom: 8,
                      cursor: 'pointer',
                      fontSize: 'var(--mantine-font-size-xs)'
                    }}
                  >
                    + Model not listed? Add it here
                  </Anchor>
                  <AppSelect
                    data={modelOptions}
                    value={values.firearmModelId ?? null}
                    onChange={(v) => setFieldValue('firearmModelId', v || null)}
                    placeholder="Search models…"
                    searchable
                    clearable
                    nothingFound="No matching models"
                  />
                </Box>
                <Box mb="md">
                  <AppText size="sm" weight={500} mb={4}>Part (optional)</AppText>
                  <AppSelect
                    data={partOptions}
                    value={values.partId ?? null}
                    onChange={(v) => setFieldValue('partId', v || null)}
                    placeholder="Search parts…"
                    searchable
                    clearable
                    nothingFound="No matching parts"
                  />
                </Box>
                <Box mb="md">
                  <AppText size="sm" weight={500} mb={4}>Ammo load (optional)</AppText>
                  <Anchor
                    component="button"
                    type="button"
                    onClick={() => handleAddAmmoEscape(values)}
                    sx={{
                      display: 'block',
                      textAlign: 'left',
                      marginBottom: 8,
                      cursor: 'pointer',
                      fontSize: 'var(--mantine-font-size-xs)'
                    }}
                  >
                    + Load not listed? Add it here
                  </Anchor>
                  <AppSelect
                    data={ammoProductOptions}
                    value={values.ammoProductId ?? null}
                    onChange={(v) => setFieldValue('ammoProductId', v || null)}
                    placeholder="Search ammo products…"
                    searchable
                    clearable
                    nothingFound="No matching products"
                    onSearchChange={(query) => {
                      if (query.length > 0) {
                        agent.AmmoProducts.list({ searchText: query, pageSize: 20 })
                          .then(result => {
                            const options = result.items.map(ap => ({
                              value: ap.id,
                              label: `${ap.brand} ${ap.name}`
                            }))
                            setAmmoProductOptions(options)
                          })
                          .catch(() => setAmmoProductOptions([]))
                      } else {
                        setAmmoProductOptions([])
                      }
                    }}
                  />
                </Box>
                <Box mb="md">
                  <AppText size="sm" color="dimmed">Save the post first, then add tags.</AppText>
                </Box>
              </>
            )}
            {isEdit && id && (
              <Box mb="md">
                <TagPicker
                  targetType={TagTargetType.Post}
                  targetId={id}
                  currentTags={formValues.tags ?? []}
                  onTagApplied={(tag: TagDto) =>
                    setFormValues((prev) => ({ ...prev, tags: [...(prev.tags ?? []), tag] }))
                  }
                  onTagRemoved={(tag: TagDto) =>
                    setFormValues((prev) => ({ ...prev, tags: (prev.tags ?? []).filter((t) => t.id !== tag.id) }))
                  }
                  aiContext={{ name: formValues.title ?? '', description: formValues.excerpt ?? undefined }}
                />
              </Box>
            )}
            <MyTextArea name="excerpt" placeholder="Short excerpt (optional)" rows={2} label="Excerpt" />
            <RichTextEditor name="body" label="Body" placeholder="Write your post…" />
            {Number(values.type) === 1 && (
              <>
                <MyTextArea
                  name="pros"
                  label="What did you like?"
                  placeholder="Pros — one per line or free form"
                  rows={3}
                />
                <MyTextArea
                  name="cons"
                  label="What didn't you like?"
                  placeholder="Cons — one per line or free form"
                  rows={3}
                />
                <MyTextInput
                  name="verdict"
                  label="Verdict"
                  placeholder="One sentence summary"
                />
              </>
            )}
            <CoverImageSection
              mode={coverImageMode}
              onModeChange={setCoverImageMode}
              coverImageUrl={values.coverImageUrl}
              setFieldValue={setFieldValue}
              uploadLoading={uploadCoverLoading}
              onUploadStart={() => setUploadCoverLoading(true)}
              onUploadEnd={() => setUploadCoverLoading(false)}
            />
            <Group mt="lg">
              <AppButton
                type="submit"
                color="ember"
                disabled={isSubmitting || (!dirty && !submitAfterRestore) || !isValid || uploadCoverLoading}
                loading={isSubmitting}
              >
                {isEdit ? 'Save changes' : 'Publish'}
              </AppButton>
            </Group>
          </Form>
        )}
      </Formik>
      </AppCard>
    </Box>
  )
})
